import { DealBuilder } from '../services/deal-builder.ts';
import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import formatter from '../../tools/formatter.js';
import moment from 'moment';

interface SummaryPageProps {
  builder: DealBuilder
}

export default function SummaryPage({ builder }: SummaryPageProps) {

  const annualDS = builder.debtService.filter(x => x.annualDS !== undefined).map(x => x.annualDS as number);
  const maxAnnualDS = Math.max(...annualDS);
  const avgAnnualDS = annualDS.reduce((prev, curr) => prev + curr, 0) / annualDS.length;
  const weightedAvgMat = builder.getWeightedAverageMaturity();

  return (
    <Stack direction="column" gap={2} margin={2} sx={{ width: "100%" }}>

      <Typography variant="subtitle2">
        <Card elevation={1}>
          <CardContent>
            <Stack direction="column" width="500px" margin="auto">
              <Stack direction="row" justifyContent="space-between">
                <Box>Rating</Box>
                <Box>{builder.ratingAssumption}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Tax Designation</Box>
                <Box>{builder.taxDesignation}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Call Option</Box>
                <Box>{builder.callDate ? builder.callDate.format("MM/DD/YYYY") : "-"}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Date of Market Conditions</Box>
                <Box>{builder.marketConditionsDate.format("MM/DD/YYYY")}</Box>
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between">
                <Box>Dated / Delivery Date</Box>
                <Box>{builder.deliveryDate.format("MM/DD/YYYY")}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>First Interest Payment</Box>
                <Box>{builder.firstCouponDate.format("MM/DD/YYYY")}</Box>
              </Stack>

              {builder.bonds.length !== 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>First Principal Payment</Box>
                    <Box>{moment(builder.bonds[0].maturity).format("MM/DD/YYYY")}</Box>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>Final Principal Payment</Box>
                    <Box>{moment(builder.bonds[builder.bonds.length - 1].maturity).format("MM/DD/YYYY")}</Box>
                  </Stack>
                </>
              )}

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between">
                <Box>Total Par Amount</Box>
                <Box>{formatter.money(builder.getTotalPrincipal(), false)}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Total Proceeds</Box>
                <Box>{formatter.money(builder.getTotalProduction(), false)}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Total Debt Service</Box>
                <Box>{formatter.money(builder.getTotalDebtService(), false)}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Total Interest</Box>
                <Box>{formatter.money(builder.getTotalInterest(), false)}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Maximum Annual Debt Service</Box>
                <Box>{formatter.money(maxAnnualDS, false)}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Average Annual Debt Service</Box>
                <Box>{formatter.money(avgAnnualDS, false)}</Box>
              </Stack>
              
              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between">
                <Box>True Interest Cost (TIC)</Box>
                <Box>{builder.tic ? formatter.percent(builder.tic, 2) : "N/A"}</Box>
              </Stack>

              {builder.ticExpenses !== undefined && builder.ticExpenses !== 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Box>Expense Adjusted TIC</Box>
                  <Box>{formatter.percent(builder.adjustedTic, 2)}</Box>
                </Stack>
              )}

              <Stack direction="row" justifyContent="space-between">
                <Box>Net Interest Cost (NIC)</Box>
                <Box>{builder.nic ? formatter.percent(builder.nic, 2) : "N/A"}</Box>
              </Stack>
              
              <Stack direction="row" justifyContent="space-between">
                <Box>Average Coupon</Box>
                <Box>{formatter.percent(builder.getAverageCoupon(), 2)}</Box>
              </Stack>
              
              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between">
                <Box>Average Life (years)</Box>
                <Box>{builder.getAverageLife().toFixed(2) + " Y"}</Box>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Box>Weighted Average Maturity (years)</Box>
                <Box>{weightedAvgMat ? weightedAvgMat.toFixed(2) + " Y" : "N/A"}</Box>
              </Stack> 
            </Stack>
          </CardContent>
        </Card>
      </Typography>
    </Stack>
  );
};
